import React, { useEffect, useRef, useState } from 'react';
import { Typography, Box } from '@mui/material';
import { motion, useInView } from 'framer-motion';

interface AnimatedCounterProps {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
  icon?: React.ReactNode;
  duration?: number;
  decimals?: number;
}

/**
 * AnimatedCounter — contador animado con useInView de framer-motion.
 * Animación de 0 al valor cuando entra en viewport.
 */
function formatCount(value: number, decimals: number) {
  if (decimals > 0) {
    return value.toLocaleString('es-PE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  return Math.floor(value).toLocaleString('es-PE');
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({
  value,
  suffix = '',
  prefix = '',
  label,
  icon,
  duration = 2,
  decimals = 0,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setCount(value);
      return;
    }

    let startTime: number | null = null;
    const animate = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(eased * value);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, value, duration]);

  return (
    <Box
      ref={ref}
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5 }}
      sx={{ textAlign: 'center', p: 2 }}
    >
      {icon && <Box sx={{ mb: 1, color: 'primary.main', display: 'flex', justifyContent: 'center' }}>{icon}</Box>}
      <Typography
        variant="h3"
        sx={{ fontWeight: 700, color: 'primary.main', fontFamily: "'DM Sans', sans-serif" }}
      >
        {prefix}{formatCount(count, decimals)}{suffix}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );
};

export default AnimatedCounter;
